import { useState, type KeyboardEvent } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { Panel } from "@/components/Panel";
import {
  useKeywords,
  useAddKeyword,
  useDeleteKeyword,
} from "@/api/stockSubresources";

interface Props {
  sku: string;
}

export function KeywordsTab({ sku }: Props) {
  const { data: rows = [], isLoading } = useKeywords(sku);
  const addOne = useAddKeyword(sku);
  const delOne = useDeleteKeyword(sku);
  const [draft, setDraft] = useState("");

  async function onAdd() {
    const v = draft.trim();
    if (!v) return;
    if (rows.some((r) => r.keyword.toLowerCase() === v.toLowerCase())) {
      toast.error("Keyword existiert bereits");
      return;
    }
    try {
      await addOne.mutateAsync(v);
      setDraft("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Anlegen fehlgeschlagen");
    }
  }

  async function onDelete(id: number) {
    try {
      await delOne.mutateAsync(id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Loeschen fehlgeschlagen");
    }
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      void onAdd();
    }
    if (e.key === "Backspace" && !draft && rows.length > 0) {
      // Letztes Keyword loeschen mit Backspace bei leerem Input
      const last = rows[rows.length - 1];
      if (last) void onDelete(last.id);
    }
  }

  const recommendedMet = rows.length >= 8;

  return (
    <Panel
      title="Keywords"
      subtitle="Fuer Suche, SEO und Marktplatz-Listings"
    >
      {isLoading ? (
        <div className="text-sm text-muted">Lade Keywords…</div>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5">
            {rows.map((k) => (
              <span
                key={k.id}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary-soft text-primary text-xs font-medium"
              >
                {k.keyword}
                <button
                  type="button"
                  onClick={() => onDelete(k.id)}
                  className="opacity-60 hover:opacity-100"
                  aria-label={`Keyword '${k.keyword}' entfernen`}
                >
                  <X className="size-3" />
                </button>
              </span>
            ))}
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKey}
              placeholder="+ Keyword (Enter)"
              className="border border-dashed border-line rounded-full px-3 py-1 text-xs bg-transparent outline-none min-w-40"
              disabled={addOne.isPending}
            />
          </div>
          <div className="mt-4 text-xs text-muted">
            {rows.length} Keywords · empfohlen ≥ 8{" "}
            {recommendedMet && (
              <span className="text-success font-semibold">✓ Erreicht</span>
            )}
          </div>
        </>
      )}
    </Panel>
  );
}
